import React, { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import { Redirect } from "react-router-dom";
import EventBus from "../common/EventBus";
import {
  Alert,
  Avatar,
  Box,
  Button,
  CircularProgress,
  Chip,
  Container,
  Divider,
  Fade,
  Grid,
  IconButton,
  InputAdornment,
  Paper,
  TextField,
  Typography,
} from "@mui/material";
import SendIcon from "@mui/icons-material/Send";
import LockResetIcon from "@mui/icons-material/LockReset";
import EmailIcon from "@mui/icons-material/Email";
import BadgeIcon from "@mui/icons-material/Badge";
import SecurityIcon from "@mui/icons-material/Security";
import FeedbackIcon from "@mui/icons-material/Feedback";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { Visibility, VisibilityOff } from "@mui/icons-material";

import userService from "../services/user.service";

const GREEN = {
  900: "#1b5e20",
  800: "#2e7d32",
  700: "#388e3c",
  600: "#43a047",
  500: "#4caf50",
  400: "#66bb6a",
  100: "#c8e6c9",
  50: "#e8f5e9",
  bg: "#f8faf9",
};

const textFieldSx = {
  "& .MuiOutlinedInput-root": {
    backgroundColor: "#fff",
    borderRadius: "10px",
    "& fieldset": { borderColor: "#e0e0e0" },
    "&:hover fieldset": { borderColor: GREEN[400] },
    "&.Mui-focused fieldset": { borderColor: GREEN[500], borderWidth: 2 },
  },
  "& .MuiInputLabel-root.Mui-focused": { color: GREEN[700] },
};

const sectionPaperSx = {
  p: { xs: 3, sm: 4 },
  borderRadius: "16px",
  border: "1px solid #eee",
  boxShadow: "0 1px 4px rgba(0,0,0,0.04)",
  transition: "box-shadow 0.25s ease",
  "&:hover": {
    boxShadow: "0 4px 20px rgba(0,0,0,0.07)",
  },
};

const SectionHeader = ({ icon, title, subtitle }) => (
  <Box sx={{ mb: 3 }}>
    <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 0.5 }}>
      <Box
        sx={{
          width: 36,
          height: 36,
          borderRadius: "10px",
          backgroundColor: GREEN[50],
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        {React.cloneElement(icon, { sx: { color: GREEN[600], fontSize: 20 } })}
      </Box>
      <Typography variant="h6" sx={{ fontWeight: 600, color: "#222", fontSize: "1.1rem" }}>
        {title}
      </Typography>
    </Box>
    {subtitle && (
      <Typography variant="body2" sx={{ color: "#888", ml: 6.5 }}>
        {subtitle}
      </Typography>
    )}
  </Box>
);

const Profile = () => {
  const { user: currentUser } = useSelector((state) => state.auth);
  const [loading, setLoading] = useState(false);
  const [notification, setNotification] = useState(null);
  const [notificationType, setNotificationType] = useState("success");
  const [feedback, setFeedback] = useState("");
  const [feedbackStatus, setFeedbackStatus] = useState(null);
  const [redirect, doRedirect] = useState(false);
  const [visible, setVisible] = useState(false);

  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);

  const handlePasswordChange = () => {
    setLoading(true);

    if (!currentPassword.trim() || !newPassword.trim()) {
      setNotification("Both current and new passwords are required.");
      setNotificationType("warning");
      setLoading(false);
      setTimeout(() => setNotification(null), 3000);
      return;
    }

    if (currentPassword === newPassword) {
      setNotification("New password cannot be the same as the current password.");
      setNotificationType("warning");
      setLoading(false);
      setTimeout(() => setNotification(null), 3000);
      return;
    }

    if (newPassword.length < 8) {
      setNotification("New password must be at least 8 characters long.");
      setNotificationType("warning");
      setLoading(false);
      setTimeout(() => setNotification(null), 3000);
      return;
    }

    var obj = {
      user: currentUser,
      oldPassword: currentPassword,
      newPassword: newPassword,
    };

    userService.changePassword(obj).then(
      (response) => {
        console.log("password response: ", response);
        setNotification("Password changed successfully!");
        setNotificationType("success");
        setLoading(false);
        setCurrentPassword("");
        setNewPassword("");
        setTimeout(() => setNotification(null), 3000);
      },
      (error) => {
        console.log("password error: ", error);
        setNotification("Something went wrong, try again.");
        setNotificationType("error");
        setLoading(false);
        setTimeout(() => setNotification(null), 3000);
      }
    );
  };

  const handleFeedbackSubmit = () => {
    setLoading(true);

    if (!feedback.trim()) {
      setFeedbackStatus({ text: "Please provide feedback before submitting.", type: "warning" });
      setLoading(false);
      setTimeout(() => setFeedbackStatus(null), 3000);
      return;
    }

    let obj = {
      user: currentUser,
      feedback: feedback,
    };

    userService.feedback(obj).then(
      (response) => {
        console.log("feedback response: ", response);
        setFeedbackStatus({
          text: "Thank you for your feedback! We will get back to you shortly.",
          type: "success",
        });
        setLoading(false);
        setFeedback("");
        setTimeout(() => setFeedbackStatus(null), 4000);
      },
      (error) => {
        console.log("feedback error: ", error);
        setFeedbackStatus({
          text: "Something went wrong, contact Evan Buss, evan@verinomics.com.",
          type: "error",
        });
        setLoading(false);
        setFeedback("");
        setTimeout(() => setFeedbackStatus(null), 5000);
      }
    );
  };

  useEffect(() => {
    if (currentUser) {
      userService.getUserBoard().then(
        () => {},
        (error) => {
          const _content =
            (error.response && error.response.data && error.response.data.message) ||
            error.message ||
            error.toString();
          console.log("error: ", _content);
          doRedirect(true);
          if (error.response && (error.response.status === 401 || error.response.status === 403)) {
            EventBus.dispatch("logout");
          }
        }
      );
    }
    const timer = setTimeout(() => setVisible(true), 60);
    return () => clearTimeout(timer);
  }, [currentUser]);

  if (!currentUser || redirect) {
    return <Redirect to="/login" />;
  }

  const initials = currentUser?.username
    ? currentUser.username.slice(0, 2).toUpperCase()
    : "?";

  return (
    <Box sx={{ minHeight: "80vh", pb: 8 }}>
      <Container maxWidth="md">
        <Fade in={visible} timeout={500}>
          <Box>
            {/* ── Profile Header ── */}
            <Paper
              elevation={0}
              sx={{
                mt: 4,
                p: { xs: 3, sm: 4 },
                borderRadius: "20px",
                background: `linear-gradient(135deg, ${GREEN[500]} 0%, ${GREEN[700]} 100%)`,
                color: "#fff",
                position: "relative",
                overflow: "hidden",
              }}
            >
              <Box
                sx={{
                  position: "absolute",
                  top: -40,
                  right: -40,
                  width: 180,
                  height: 180,
                  borderRadius: "50%",
                  backgroundColor: "rgba(255,255,255,0.08)",
                }}
              />
              <Box
                sx={{
                  position: "absolute",
                  bottom: -30,
                  left: -30,
                  width: 120,
                  height: 120,
                  borderRadius: "50%",
                  backgroundColor: "rgba(255,255,255,0.06)",
                }}
              />

              <Box sx={{ display: "flex", alignItems: "center", gap: 3, position: "relative", zIndex: 1 }}>
                <Avatar
                  src={currentUser?.gravatar}
                  alt={currentUser?.username}
                  sx={{
                    width: 80,
                    height: 80,
                    fontSize: "1.8rem",
                    fontWeight: 700,
                    backgroundColor: "rgba(255,255,255,0.2)",
                    border: "3px solid rgba(255,255,255,0.4)",
                    backdropFilter: "blur(4px)",
                  }}
                >
                  {initials}
                </Avatar>

                <Box>
                  <Typography variant="h4" sx={{ fontWeight: 700, letterSpacing: "-0.02em", mb: 0.5 }}>
                    {currentUser?.username}
                  </Typography>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
                    <Chip
                      icon={<EmailIcon sx={{ fontSize: 16, color: "rgba(255,255,255,0.85) !important" }} />}
                      label={currentUser?.email}
                      size="small"
                      sx={{
                        backgroundColor: "rgba(255,255,255,0.15)",
                        color: "rgba(255,255,255,0.95)",
                        fontWeight: 500,
                        fontSize: "0.8rem",
                        backdropFilter: "blur(4px)",
                        "& .MuiChip-icon": { color: "rgba(255,255,255,0.85)" },
                      }}
                    />
                    {currentUser?.roles?.map((role) => (
                      <Chip
                        key={role}
                        icon={<CheckCircleIcon sx={{ fontSize: 14, color: "rgba(255,255,255,0.8) !important" }} />}
                        label={role.replace("ROLE_", "")}
                        size="small"
                        sx={{
                          backgroundColor: "rgba(255,255,255,0.15)",
                          color: "rgba(255,255,255,0.9)",
                          fontWeight: 500,
                          fontSize: "0.75rem",
                          backdropFilter: "blur(4px)",
                        }}
                      />
                    ))}
                  </Box>
                </Box>
              </Box>
            </Paper>

            {/* ── Loading indicator ── */}
            {loading && (
              <Box sx={{ display: "flex", justifyContent: "center", mt: 3 }}>
                <CircularProgress size={32} sx={{ color: GREEN[500] }} />
              </Box>
            )}

            {/* ── Content Grid ── */}
            <Grid container spacing={3} sx={{ mt: 1 }}>
              {/* ── Account Details Card ── */}
              <Grid item xs={12} md={5}>
                <Paper elevation={0} sx={sectionPaperSx}>
                  <SectionHeader
                    icon={<BadgeIcon />}
                    title="Account Details"
                    subtitle="Your account information"
                  />
                  <Divider sx={{ mb: 3 }} />

                  <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}>
                    <Box>
                      <Typography variant="caption" sx={{ color: "#999", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.06em", fontSize: "0.7rem" }}>
                        Username
                      </Typography>
                      <Typography variant="body1" sx={{ fontWeight: 500, color: "#333", mt: 0.3 }}>
                        {currentUser?.username}
                      </Typography>
                    </Box>

                    <Box>
                      <Typography variant="caption" sx={{ color: "#999", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.06em", fontSize: "0.7rem" }}>
                        Email
                      </Typography>
                      <Typography variant="body1" sx={{ fontWeight: 500, color: "#333", mt: 0.3 }}>
                        {currentUser?.email}
                      </Typography>
                    </Box>

                    <Box>
                      <Typography variant="caption" sx={{ color: "#999", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.06em", fontSize: "0.7rem" }}>
                        User ID
                      </Typography>
                      <Typography variant="body2" sx={{ fontFamily: "monospace", color: "#666", mt: 0.3, fontSize: "0.85rem" }}>
                        {currentUser?.id}
                      </Typography>
                    </Box>

                    <Box>
                      <Typography variant="caption" sx={{ color: "#999", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.06em", fontSize: "0.7rem" }}>
                        Roles
                      </Typography>
                      <Box sx={{ display: "flex", gap: 0.8, mt: 0.5, flexWrap: "wrap" }}>
                        {currentUser?.roles?.map((role) => (
                          <Chip
                            key={role}
                            label={role.replace("ROLE_", "")}
                            size="small"
                            sx={{
                              backgroundColor: GREEN[50],
                              color: GREEN[800],
                              fontWeight: 600,
                              fontSize: "0.72rem",
                              border: `1px solid ${GREEN[100]}`,
                            }}
                          />
                        ))}
                      </Box>
                    </Box>
                  </Box>
                </Paper>
              </Grid>

              {/* ── Right Column ── */}
              <Grid item xs={12} md={7}>
                <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
                  {/* ── Change Password Card ── */}
                  <Paper elevation={0} sx={sectionPaperSx}>
                    <SectionHeader
                      icon={<SecurityIcon />}
                      title="Change Password"
                      subtitle="Keep your account secure"
                    />
                    <Divider sx={{ mb: 3 }} />

                    <TextField
                      label="Current Password"
                      type={showCurrentPassword ? "text" : "password"}
                      fullWidth
                      variant="outlined"
                      size="small"
                      value={currentPassword}
                      onChange={(e) => setCurrentPassword(e.target.value)}
                      sx={{ ...textFieldSx, mb: 2 }}
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton
                              onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                              edge="end"
                              size="small"
                            >
                              {showCurrentPassword ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                            </IconButton>
                          </InputAdornment>
                        ),
                      }}
                    />

                    <TextField
                      label="New Password"
                      type={showNewPassword ? "text" : "password"}
                      fullWidth
                      variant="outlined"
                      size="small"
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      sx={{ ...textFieldSx, mb: 2.5 }}
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton
                              onClick={() => setShowNewPassword(!showNewPassword)}
                              edge="end"
                              size="small"
                            >
                              {showNewPassword ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                            </IconButton>
                          </InputAdornment>
                        ),
                      }}
                    />

                    <Button
                      variant="contained"
                      disableElevation
                      startIcon={<LockResetIcon />}
                      onClick={handlePasswordChange}
                      disabled={loading}
                      sx={{
                        backgroundColor: GREEN[500],
                        color: "#fff",
                        fontWeight: 600,
                        textTransform: "none",
                        borderRadius: "10px",
                        px: 3,
                        py: 1,
                        fontSize: "0.9rem",
                        "&:hover": { backgroundColor: GREEN[600] },
                      }}
                    >
                      Update Password
                    </Button>

                    {notification && (
                      <Fade in>
                        <Alert
                          severity={notificationType}
                          variant="filled"
                          sx={{ mt: 2, borderRadius: "10px" }}
                        >
                          {notification}
                        </Alert>
                      </Fade>
                    )}
                  </Paper>

                  {/* ── Feedback Card ── */}
                  <Paper elevation={0} sx={sectionPaperSx}>
                    <SectionHeader
                      icon={<FeedbackIcon />}
                      title="Send Feedback"
                      subtitle="Help us improve — our team at Verinomics reviews every message"
                    />
                    <Divider sx={{ mb: 3 }} />

                    <TextField
                      label="Your Feedback"
                      multiline
                      rows={4}
                      fullWidth
                      variant="outlined"
                      value={feedback}
                      onChange={(e) => setFeedback(e.target.value)}
                      placeholder="Tell us what you think, report a bug, or request a feature..."
                      sx={{ ...textFieldSx, mb: 2.5 }}
                    />

                    <Button
                      variant="contained"
                      disableElevation
                      startIcon={<SendIcon />}
                      onClick={handleFeedbackSubmit}
                      disabled={loading}
                      sx={{
                        backgroundColor: GREEN[500],
                        color: "#fff",
                        fontWeight: 600,
                        textTransform: "none",
                        borderRadius: "10px",
                        px: 3,
                        py: 1,
                        fontSize: "0.9rem",
                        "&:hover": { backgroundColor: GREEN[600] },
                      }}
                    >
                      Send Feedback
                    </Button>

                    {feedbackStatus && (
                      <Fade in>
                        <Alert
                          severity={feedbackStatus.type}
                          variant="filled"
                          sx={{ mt: 2, borderRadius: "10px" }}
                        >
                          {feedbackStatus.text}
                        </Alert>
                      </Fade>
                    )}
                  </Paper>
                </Box>
              </Grid>
            </Grid>
          </Box>
        </Fade>
      </Container>
    </Box>
  );
};

export default Profile;
